import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Lock, Mail, AlertCircle } from 'lucide-react';
import { useFirebaseAuth } from '../../hooks/useFirebaseAuth';

export function MobileLoginPage() {
  const navigate = useNavigate();
  const { user, loading, login } = useFirebaseAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  if (user && !loading) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Erro ao fazer login:', err);
      setError('Credenciais inválidas. Por favor, tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0B1A] flex items-center justify-center">
        <p className="text-white">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0B1A] flex flex-col p-4">
      {/* Logo e Título */}
      <div className="text-center mt-16 mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-600 mb-4">
          <Lock className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white">Painel Administrativo</h1>
        <p className="text-gray-400 mt-2">Faça login para acessar o sistema</p>
      </div>

      {/* Formulário */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
            E-mail
          </label>
          <div className="relative">
            <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#2C2D3C] text-white rounded-lg pl-10 pr-4 py-3 border border-gray-700 focus:border-indigo-500 focus:outline-none"
              placeholder="Seu e-mail"
              required
              disabled={isLoading}
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-400 mb-2">
            Senha
          </label>
          <div className="relative">
            <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#2C2D3C] text-white rounded-lg pl-10 pr-4 py-3 border border-gray-700 focus:border-indigo-500 focus:outline-none"
              placeholder="Sua senha"
              required
              disabled={isLoading}
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-indigo-600 text-white rounded-lg py-3 font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Entrando...' : 'Entrar'}
        </button>

        <div className="flex justify-between text-sm">
          <a href="#" className="text-indigo-400 hover:text-indigo-300">
            Esqueceu sua senha?
          </a>
          <a href="#" className="text-indigo-400 hover:text-indigo-300">
            Precisa de ajuda?
          </a>
        </div>
      </form>

      <p className="text-center text-gray-400 text-sm mt-8">
        © {new Date().getFullYear()} Neurosaber. Todos os direitos reservados.
      </p>
    </div>
  );
}